import React, { useState } from 'react';
import MyDataTable from '../../components/MyDataTable';
import { Pagination } from '../../components/Pagination';
import { translate } from '../../services/translate';
import { SignaturesParamList } from '../../typings/Params';
import { RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { ClientError, GenericPaginatedParameters, NotFound } from '../../services/central-api/base-service';
import { signaturesService } from '../../services/central-api/signatures';
import moment from 'moment';
import DeleteModal from '../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { AlreadySignedReport } from '../../services/central-api/dispatch';
import ConfirmIdentityModal from '../../components/ConfirmIdentityModal';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Card from '../../components/Card';
import DataTableHeader from '../../components/DataTableHeader';
import DataTableContainer from '../../components/DataTableContainer';
import DropdownButton from '../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import { openExternalLinkInNewTab, openInNewTab } from '../../services/navigation-assistant';
import Badge, { BadgeColor } from '../../components/Badge';
import { View } from 'react-native';
import TouchableOpacityWithMiddleClick from '../../components/TouchableOpacityWithMiddleClick';
import { RootNavigation } from '../../Routes';
import MyAppText from '../../components/MyAppText';

export default function Signatures() {
    const route = useRoute<RouteProp<SignaturesParamList, 'Signatures'>>();
    const navigation = useNavigation<RootNavigation>();

    const [confirmSignModalVisible, setConfirmSignModalVisible] = useState(false);
    const [selectedSignableReport, setSelectedSignableReport] = useState<SignableReport>();
    const [isConfirmIdentityModalVisible, setIsConfirmIdentityModalVisible] = useState(false);

    const [filters, setFilters] = useState<GenericPaginatedParameters>({
        page: Math.max(route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });

    const isFocused = useIsFocused();
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['getPaginatedSupervisorPendingSignatures', filters],
        queryFn: () => signaturesService.getPaginatedSupervisorPendingSignatures(filters),
        enabled: isFocused,
        refetchInterval: isFocused ? 30000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    async function supervisorSignReport(signableReportId: number, params: { password: string; }) {
        try {
            await signaturesService.supervisorSignReport(signableReportId, params);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            refetch();
            return 'success';

        } catch (err) {
            if (err instanceof AlreadySignedReport) {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message, { userName: err.userName }),
                });
                return 'failure';

            } else if (err instanceof NotFound) {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
                return 'failure';
            } else if (err instanceof ClientError) {
                if (err.message === 'InvalidValue') {
                    Toast.show({
                        type: 'sentinelxError',
                        text1: translate('invalidPassword'),
                    });
                    return 'failure';
                }
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
                return 'failure';
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return 'failure';

        }
    }

    async function openReportUrl(signableReport: SignableReport) {
        try {
            const res = await signaturesService.getSignableReportResultUrl(signableReport.id);
            openExternalLinkInNewTab(res.resultUrl);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }

    }

    function getReportBadge(signableReport: SignableReport) {
        let color: BadgeColor = 'transparencyBlack';
        let message = '';

        if (signableReport.reportType === 'occurrence') {
            color = 'gray';
            message = translate('occurrenceReportAcronym');
        } else if (signableReport.reportType === 'rdp_ras') {
            color = 'blue';
            message = `${translate('activityAndServiceAcronym')}/${translate('policePatrolScheduleAcronym')}`;
        }

        return <View>
            <Badge mode='SQUARE' color={color}>{message}</Badge>
        </View>;
    }

    function getReportProtocolIdByType(signableReport: SignableReport) {
        if (signableReport.reportType === 'occurrence') {
            return signableReport.occurrenceId;
        } else if (signableReport.reportType === 'rdp_ras') {
            return signableReport.unitId;
        }
        return undefined;
    }

    function getReportType(signableReport: SignableReport) {
        if (signableReport.reportType === 'occurrence') {
            return translate('occurrenceReportAcronym');
        } else if (signableReport.reportType === 'rdp_ras') {
            return `${translate('activityAndServiceAcronym')}/${translate('policePatrolScheduleAcronym')}`;
        }
        return undefined;
    }

    function referenceNumberPressed(row: SignableReport) {
        row.reportType === 'occurrence'
            ? openInNewTab('Dispatch', { screen: 'DispatchOccurrenceDetail', params: { occurrenceId: row.occurrenceId } })
            : openInNewTab('Dispatch', { screen: 'UnitForm', params: { id: row.unitId } });
    }

    return <Container>
        <Header title={translate('signatures')} />
        <Card>
            <DataTableHeader
                title={translate('pendingSignableReports')}
                zIndex={2}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('type'),
                        cell: row => getReportBadge(row)
                    }, {
                        name: translate('referenceNumber'),
                        cell: row => <View style={{ flexDirection: 'row', gap: 3 }} >
                            <MyAppText >{row.reportType === 'occurrence' ? `${translate('nProtocol')}:` : row.reportType === 'rdp_ras' ? `${translate('team')}:` : ''}</MyAppText>
                            <TouchableOpacityWithMiddleClick
                                onPress={() => referenceNumberPressed(row)}
                                onMiddleClick={() => referenceNumberPressed(row)}
                            >
                                <MyAppText style={{ textDecorationLine: 'underline' }}>{row.reportType === 'occurrence'
                                    ? `#${row.occurrenceId}`
                                    : row.reportType === 'rdp_ras'
                                        ? `#${row.unitId}`
                                        : ''
                                }
                                </MyAppText>
                            </TouchableOpacityWithMiddleClick>

                        </View>

                    },
                    {
                        name: translate('status'),
                        selector: row => translate(row.status),
                    },
                    {
                        name: translate('signedOn'),
                        selector: row => moment(row.teamSignedOn || '').format('DD/MM/YYYY HH:mm:ss'),
                    },
                    {
                        name: translate('signedBy'),
                        selector: row => `${row.teamSignedByUser?.warName ?? row.teamSignedByUser?.name} (RF ${row.teamSignedByUser?.registry})`,
                        grow: 2,
                    },
                    {
                        name: translate('unit'),
                        selector: row => `${row.actingBodyUnit.actingBody.name}/${row.actingBodyUnit.name}`,
                    },
                    {
                        name: translate('actions'),
                        button: true,
                        cell: row => <DropdownButton
                            actions={[{
                                content: translate('viewReport'),
                                onPress: () => openReportUrl(row),
                            }, {
                                content: translate('signReport'),
                                onPress: () => {
                                    setSelectedSignableReport(row);
                                    setConfirmSignModalVisible(true);
                                },
                            }]}
                        />,
                        wrap: true,
                    }
                    ]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                    noResultsText={translate('noReportWithPendingSignature')}
                />}
                pagination={<Pagination
                    totalItems={data?.count ?? 0}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>

        {selectedSignableReport && <DeleteModal
            setModalVisible={setConfirmSignModalVisible}
            isModalVisible={confirmSignModalVisible}
            itemName={translate('object')}
            customConfirm={translate('sign')}
            customMessage={selectedSignableReport.reportType === 'occurrence'
                ? translate('confirmSignature', { documentType: getReportType(selectedSignableReport!), occurrenceId: getReportProtocolIdByType(selectedSignableReport!) })
                : translate('confirmTeamSignature', { documentType: getReportType(selectedSignableReport!), teamId: getReportProtocolIdByType(selectedSignableReport!) })
            }
            onSubmitType='primary'
            onSubmit={async () => {
                setIsConfirmIdentityModalVisible(true);
            }}
        />}
        <ConfirmIdentityModal
            isModalVisible={isConfirmIdentityModalVisible}
            setModalVisible={setIsConfirmIdentityModalVisible}
            onSubmit={async (password) => {
                if (!selectedSignableReport) {
                    return 'success';
                }
                return await supervisorSignReport(selectedSignableReport.id, { password });
            }}

        />
    </Container>;
}
